/**
 * Bridges between PatientDetailsForm's save body and the AI-transcript
 * captured-item payload.
 *
 * Patient details is a SINGLE-RECORD UPDATE section (unlike CC / Dx /
 * Med which APPEND rows). One and only one captured item exists per
 * transcript — its uniqueKey is fixed at `patient_details:main`, and
 * every subsequent save (manual or AI) UPSERTS onto the same row.
 *
 * Both entry points funnel here:
 *   • Manual card save from the transcript-mode PatientDetailsForm
 *     hands the same wire body it hands PatientService in standalone
 *     mode (`buildProfileUpdateBody`'s output).
 *   • The AI agent's `update_patient_details` tool emits a subset of
 *     the same field names on its captured payload; the Go adapter
 *     merges them onto the current record at commit time.
 *
 * We deliberately DO NOT carry the read-only fields (phoneNo, work
 * email, countryCode, profileImage, age) into the captured payload —
 * those are re-hydrated from the current record on the commit side,
 * so a stale captured payload can never accidentally clear them.
 */

/**
 * PatientDetailsForm.submit body → captured "update" payload.
 *
 * body shape (from `buildProfileUpdateBody`):
 *   {
 *     patientId, firstName, lastName, gender, email, dateOfBirth,
 *     phoneNo, countryCode, profileImage, age,
 *   }
 *
 * Returned payload keeps only the editable fields — the ones the
 * reviewer can actually change on the form. Empty strings are kept
 * verbatim so a deliberate clear ("no last name") survives.
 */
export function bodyToCapturedUpdate(body) {
  const b = body || {};
  return {
    firstName: b.firstName != null ? String(b.firstName) : "",
    lastName: b.lastName != null ? String(b.lastName) : "",
    gender: b.gender,
    dateOfBirth: b.dateOfBirth != null ? String(b.dateOfBirth) : "",
    email: b.email != null ? String(b.email) : "",
  };
}

/**
 * Overlay a captured-item payload onto the current patient object so
 * the form seeds from what will actually be committed.
 *
 * `patient` is the standalone record shape (`firstName`, `lastName`,
 * `email`, `dateOfBirth`, `gender`, `phoneno` — note the lowercase `o`
 * on phoneno, unrelated to the wire body's `phoneNo`).
 * `payload` is one of the shapes `bodyToCapturedUpdate` emits (or a
 * partial from the AI agent's `update_patient_details` tool).
 */
export function overlayCapturedOnPatient(patient, payload) {
  const p = patient || {};
  const c = payload || {};
  return {
    ...p,
    firstName: c.firstName != null && c.firstName !== "" ? c.firstName : p.firstName,
    lastName: c.lastName != null ? c.lastName : p.lastName,
    gender: c.gender != null ? c.gender : p.gender,
    dateOfBirth: c.dateOfBirth != null && c.dateOfBirth !== "" ? c.dateOfBirth : p.dateOfBirth,
    email: c.email != null ? c.email : p.email,
  };
}
